import type { OverviewResponse } from "contracts/api/dashboard/responses";
import {
  BRIEF_DETAIL_KIND,
  BRIEF_SOURCE_TYPE,
  BRIEF_STATUS,
} from "../../enums/brief";
import { MAP_EVIDENCE_KIND } from "../../enums/map";
import { mapEvidenceLabel } from "../map/mapEvidenceLabels";
import type { MapBriefHandoff } from "../map/mapTypes";
import {
  pageDisplayLabel,
  rawPagePath,
} from "../pages/boundaries/page-label.parser";
import type { DeliveryBrief } from "./briefTypes";

export function buildMapBrief(
  data: OverviewResponse,
  handoff: MapBriefHandoff,
): DeliveryBrief {
  const pages = handoff.pages.map((page) => ({
    id: page.id,
    title: pageDisplayLabel(page, data.site),
    path: rawPagePath(page),
    detailRoute: `/w/${data.site.websiteId}/scan/pages/${page.id}`,
    contentFitnessScore: page.contentFitness?.score ?? null,
    assessed: page.contentFitness !== null,
  }));
  const relationshipItems = handoff.relationships.length
    ? handoff.relationships
    : pages.map(
        (page) => `Provide a clear route to ${page.title} (${page.path}).`,
      );
  return {
    id: `map-${handoff.changeId}`,
    sourceType: BRIEF_SOURCE_TYPE.MAP,
    title: handoff.title,
    objective: handoff.objective,
    currentProblem: handoff.currentProblem,
    requiredChange: handoff.agreedChange,
    scopeNotes: `${pages.length} represented ${pages.length === 1 ? "page" : "pages"} in ${handoff.groups.join(" / ")}.`,
    outOfScopeNotes:
      "Final page copy, approved taxonomy, redirect rules, audience definitions and effort estimates are not part of this Preview.",
    requirements: [
      {
        id: "target-role",
        title: "Target page role",
        items: [handoff.targetRole],
      },
      {
        id: "required-relationships",
        title: "Required relationships and destinations",
        items: relationshipItems,
      },
    ],
    acceptanceCriteria: handoff.acceptanceCriteria,
    openQuestions: handoff.openQuestions.map((text, index) => ({
      id: `map-question-${index}`,
      text,
      response: "",
      confirmationSource: "",
    })),
    owner: handoff.owner,
    status: BRIEF_STATUS.NEEDS_INPUT,
    pages,
    source: {
      type: BRIEF_SOURCE_TYPE.MAP,
      id: handoff.changeId,
      label: "Map change",
      title: handoff.title,
      route: `/w/${data.site.websiteId}/map`,
      dimension: null,
      evidence: handoff.evidence.map((evidence, index) => ({
        id: `map-evidence-${index}`,
        label: mapEvidenceLabel(evidence),
        detail: evidence.detail,
        kind:
          evidence.kind === MAP_EVIDENCE_KIND.INFERENCE
            ? BRIEF_DETAIL_KIND.TECHNICAL
            : BRIEF_DETAIL_KIND.READABLE,
        route: evidence.issueId
          ? `/w/${data.site.websiteId}/scan/issues/${evidence.issueId}`
          : undefined,
      })),
      guidance: [],
      routes: [
        { label: "Return to Map", href: `/w/${data.site.websiteId}/map` },
        ...handoff.issues.map((issue) => ({
          label: "Open linked issue",
          href: `/w/${data.site.websiteId}/scan/issues/${issue.id}`,
        })),
      ],
      technical: [
        { label: "Map change ID", value: handoff.changeId },
        { label: "Change type", value: handoff.changeType },
        { label: "Current group", value: handoff.currentGroup },
      ],
      coverageNote: pages.some((page) => !page.assessed)
        ? "This brief includes pages that were not assessed."
        : `${pages.length} represented ${pages.length === 1 ? "page has" : "pages have"} assessment data.`,
    },
  };
}
